// route.js — the app's location, as a URL hash.
//
// The whole screen position is three values — the global tab, the open job and
// the tab inside that job — and this module is the only place that knows how
// they are spelled in the address bar:
//
//   #/dashboard   #/jobs   #/prices   #/settings   #/approvals
//   #/jobs/<jobId>                 (Summary)
//   #/jobs/<jobId>/materials       (| invoices | issues)
//
// parseRoute / formatRoute are pure translation (no window) and carry the
// rules; useRoute at the bottom is the only code that touches window.history.
//
// A hash, not a path — see DECISIONS D-019. Anything unrecognized parses to the
// dashboard rather than throwing: a mistyped or stale link must still open the
// app.

import React from 'react';

export const VIEWS = ['dashboard', 'jobs', 'prices', 'settings', 'approvals'];
export const JOB_TABS = ['summary', 'materials', 'invoices', 'issues'];

const safeDecode = (s) => {
  try { return decodeURIComponent(s); } catch (_) { return s; }
};

// hash → { view, jobId, jobTab }. jobId is null outside a job; jobTab is always
// a valid tab so callers never have to default it.
export function parseRoute(hash) {
  const parts = String(hash || '')
    .replace(/^#/, '')
    .split(/[?&]/)[0]
    .split('/')
    .filter(Boolean)
    .map(safeDecode);
  const view = VIEWS.includes(parts[0]) ? parts[0] : 'dashboard';
  if (view === 'jobs' && parts[1]) {
    return { view, jobId: parts[1], jobTab: JOB_TABS.includes(parts[2]) ? parts[2] : 'summary' };
  }
  return { view, jobId: null, jobTab: 'summary' };
}

// { view, jobId, jobTab } → hash. The inverse of parseRoute for every route it
// can return; Summary is the bare job URL so the common link stays short.
export function formatRoute(route) {
  const r = route || {};
  if (r.jobId) {
    const tab = JOB_TABS.includes(r.jobTab) && r.jobTab !== 'summary' ? `/${r.jobTab}` : '';
    return `#/jobs/${encodeURIComponent(r.jobId)}${tab}`;
  }
  return `#/${VIEWS.includes(r.view) ? r.view : 'dashboard'}`;
}

export function sameRoute(a, b) {
  return formatRoute(a) === formatRoute(b);
}

// [route, go] — the current route, kept in step with the address bar.
//
// go(next) is how the app navigates: each call becomes a browser history entry,
// which is what makes the back and forward buttons work. `{ replace: true }`
// rewrites the current entry instead, for a URL that turned out to point at
// nothing. Going where you already are adds no entry.
export function useRoute() {
  const [route, setRoute] = React.useState(() => parseRoute(window.location.hash));

  const go = React.useCallback((next, { replace = false } = {}) => {
    const target = parseRoute(formatRoute(next));
    if (!sameRoute(target, parseRoute(window.location.hash))) {
      window.history[replace ? 'replaceState' : 'pushState'](null, '', formatRoute(target));
    }
    // pushState does not fire popstate, so the state is set here as well.
    setRoute((prev) => (sameRoute(prev, target) ? prev : target));
  }, []);

  // Back / forward (and a hand-edited hash) change the URL without going
  // through go() — follow it.
  React.useEffect(() => {
    const sync = () => {
      const target = parseRoute(window.location.hash);
      setRoute((prev) => (sameRoute(prev, target) ? prev : target));
    };
    window.addEventListener('popstate', sync);
    window.addEventListener('hashchange', sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('hashchange', sync);
    };
  }, []);

  return [route, go];
}
